import JSZip from "jszip";
import Delaunator from "delaunator";
import type { CaptureFrame, CaptureSession, ExportExtras } from "./types";
import { buildFbx, buildStaticFbx } from "./fbx";
import { makeC4dRigScript } from "./c4dRigScript";

function csvEscape(value: string | number): string {
  const stringValue = String(value);
  if (!/[",\n]/.test(stringValue)) return stringValue;
  return `"${stringValue.replaceAll('"', '""')}"`;
}

export function makeBlendshapeCsv(frames: CaptureFrame[]): string {
  const names = Array.from(
    new Set(frames.flatMap((frame) => frame.blendshapes.map((item) => item.name))),
  ).sort();

  const rows = [["frame", "time_ms", ...names].map(csvEscape).join(",")];
  for (const frame of frames) {
    const scores = new Map(frame.blendshapes.map((item) => [item.name, item.score]));
    rows.push(
      [frame.index, frame.timeMs.toFixed(2), ...names.map((name) => scores.get(name) ?? 0)]
        .map(csvEscape)
        .join(","),
    );
  }

  return `${rows.join("\n")}\n`;
}

export function makeLandmarkCsv(frames: CaptureFrame[]): string {
  const rows = ["frame,time_ms,landmark,x,y,z,visibility"];

  for (const frame of frames) {
    frame.landmarks.forEach((landmark, index) => {
      rows.push(
        [
          frame.index,
          frame.timeMs.toFixed(2),
          index,
          landmark.x.toFixed(7),
          landmark.y.toFixed(7),
          landmark.z.toFixed(7),
          (landmark.visibility ?? 0).toFixed(7),
        ].join(","),
      );
    });
  }

  return `${rows.join("\n")}\n`;
}

export function makePoseLandmarkCsv(frames: CaptureFrame[]): string {
  const rows = ["frame,time_ms,landmark,x,y,z,visibility"];

  for (const frame of frames) {
    frame.poseLandmarks?.forEach((landmark, index) => {
      rows.push(
        [
          frame.index,
          frame.timeMs.toFixed(2),
          index,
          landmark.x.toFixed(7),
          landmark.y.toFixed(7),
          landmark.z.toFixed(7),
          (landmark.visibility ?? 0).toFixed(7),
        ].join(","),
      );
    });
  }

  return `${rows.join("\n")}\n`;
}

function enabledExtras(options: ExportExtras | undefined): string[] {
  if (!options) return [];
  return Object.entries(options)
    .filter(([, enabled]) => enabled)
    .map(([name]) => name);
}

export function getTriangles(frame: CaptureFrame | undefined): number[] {
  if (!frame) return [];
  const points = frame.landmarks.map((landmark) => [landmark.x, landmark.y] as [number, number]);
  const triangulation = Delaunator.from(points);
  const triangles = Array.from(triangulation.triangles);
  const filtered: number[] = [];

  for (let index = 0; index + 2 < triangles.length; index += 3) {
    const aIndex = triangles[index];
    const bIndex = triangles[index + 1];
    const cIndex = triangles[index + 2];
    const a = points[aIndex];
    const b = points[bIndex];
    const c = points[cIndex];
    if (a && b && c && maxEdge(a, b, c) < 0.18) {
      filtered.push(aIndex, bIndex, cIndex);
    }
  }

  return filtered;
}

function maxEdge(a: [number, number], b: [number, number], c: [number, number]): number {
  return Math.max(distance(a, b), distance(b, c), distance(c, a));
}

function distance(a: [number, number], b: [number, number]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

function landmarkToWorld(landmark: { x: number; y: number; z: number }): [number, number, number] {
  return [
    (landmark.x - 0.5) * 180,
    (0.5 - landmark.y) * 130,
    -landmark.z * 220,
  ];
}

function makeObj(frame: CaptureFrame, triangles: number[]): string {
  const lines = [
    "# MayoFlux Face Capture static OBJ fallback",
    "# Import this if a host application rejects the FBX container.",
    "o FaceMesh_Static",
  ];

  for (const landmark of frame.landmarks) {
    const [x, y, z] = landmarkToWorld(landmark);
    lines.push(`v ${x.toFixed(6)} ${y.toFixed(6)} ${z.toFixed(6)}`);
  }

  for (let index = 0; index + 2 < triangles.length; index += 3) {
    lines.push(`f ${triangles[index] + 1} ${triangles[index + 1] + 1} ${triangles[index + 2] + 1}`);
  }

  return `${lines.join("\n")}\n`;
}

export async function buildCaptureZip(session: CaptureSession, exportExtras?: ExportExtras): Promise<Blob> {
  if (!session.frames.length) {
    throw new Error("No capture frames available to export.");
  }

  const zip = new JSZip();
  const triangles = getTriangles(session.frames[0]);
  const extras = enabledExtras(exportExtras);
  const hasPoseLandmarks = session.frames.some((frame) => frame.poseLandmarks?.length);

  zip.file("face-capture.fbx", buildFbx(session, triangles, { extras: exportExtras }));
  zip.file("face-capture-static.fbx", buildStaticFbx(session, triangles, exportExtras));
  zip.file("face-capture.obj", makeObj(session.frames[0], triangles));
  zip.file("capture.json", JSON.stringify({ ...session, triangles, exportExtras }, null, 2));
  zip.file("c4d_build_puppet_rig.py", makeC4dRigScript());
  zip.file("blendshapes.csv", makeBlendshapeCsv(session.frames));
  zip.file("landmarks.csv", makeLandmarkCsv(session.frames));
  if (hasPoseLandmarks) {
    zip.file("pose_landmarks.csv", makePoseLandmarkCsv(session.frames));
  }
  zip.file(
    "README.txt",
    [
      "MayoFlux Face Capture",
      "",
      "Cinema 4D first path:",
      "1. Extract this ZIP.",
      "2. In Cinema 4D, open Script Manager and run c4d_build_puppet_rig.py.",
      "3. When prompted, select capture.json from this same export folder.",
      "4. The script builds FaceMesh_Animated, Face_Landmark_Nulls, BodyPose_Nulls when present, and Puppet_Controls.",
      "",
      "The C4D rig is driven from capture.json because Cinema 4D can drop or ignore the animated vertex/null data in browser-authored FBX files.",
      "face-capture.fbx and face-capture-static.fbx remain included as interchange fallbacks, but the C4D script is the preferred path.",
      "face-capture.obj is included as a static geometry fallback only.",
      extras.length
        ? `Selected anatomy included: ${extras.join(", ")}.${hasPoseLandmarks ? " Body pose landmarks are exported as animated POSE_ nulls plus pose_landmarks.csv." : " Body pose data is only recorded when neck, torso, arms, or full body is selected while recording."}`
        : "No proxy anatomy was selected for this export.",
      "capture.json is the full raw recording and should be treated as the source of truth.",
      hasPoseLandmarks
        ? "blendshapes.csv, landmarks.csv, and pose_landmarks.csv are included for cleanup, retargeting, and custom C4D scripts."
        : "blendshapes.csv and landmarks.csv are included for cleanup, retargeting, and custom C4D scripts.",
      "",
      `Frames: ${session.frames.length}`,
      `Source: ${session.source.width}x${session.source.height}`,
      `FPS estimate: ${session.source.fpsEstimate.toFixed(2)}`,
    ].join("\n"),
  );

  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}
